import React, { useRef, useEffect, useState } from "react";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Button } from "../../components/ui/button";
import { X, Download, FileText } from "lucide-react";
import { usePdfGenerator } from "./usePdfGenerator";
import { useDocxGenerator } from "./useDocxGenerator";
import { CompanyProfileSection } from "./sections/CompanyProfileSection";
import { BusinessModelSection } from "./sections/BusinessModelSection";
import { OpenChargesSection } from "./sections/OpenChargesSection";
import { DirectorDetailsSection } from "./sections/DirectorDetailsSection";
import { KeyRatiosSection } from "./sections/KeyRatiosSection";
import { GstDetailsSection } from "./sections/GstDetailsSection";
import { ShareholdingSection } from "./sections/ShareholdingSection";
import { DebtProfileSection } from "./sections/DebtProfileSection";
import Colour_Astrafin from "../../assets/Colour-Astrafin.png";
import { useParams } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { onePageModelData } from "../../types/CustomerInterface";
import apiService from "../../services/apiService";
import SecuritiesAllotmentSection from "./sections/SecuritiesAllotmentSection";
import RelatedPartySection from "../RelatedPartySection";
import LLPContributionDetailsSection from "./sections/LLPContributionDetailsSection";

interface OnePagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  CustomerData?: onePageModelData;
}

interface ApiResponse<T> {
  data: T[];
  selectedDate:Date;
}

interface DebtData {
  key: string;
  sr?: number;
  nature: string;
  loanType: string;
  bankName: string;
  totalLoanAmount: number;
  emi: number;
  loanStartDate: string;
  roi: number;
  outstandingAsOnDate: number;
  totalTenure: number;
}

const OnePagerModal: React.FC<OnePagerModalProps> = ({ isOpen, onClose, CustomerData }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const { generatePdf } = usePdfGenerator(contentRef);
  const { generateDocx } = useDocxGenerator();
  const [shareDebtData, setShareDebtData] = useState<DebtData[]>([]);
  const { customerId } = useParams<{ customerId: string }>();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const fetchDebt = () => {
    apiService
      .get<ApiResponse<DebtData>>(`/customers/debts?customerId=${customerId}`)
      .then((response) => {
        setShareDebtData(response.data);
        if (response.selectedDate) {
          setSelectedDate(new Date(response.selectedDate)); // ✅ ensure it's a Date object
        }
      });
  };

  useEffect(() => {
    fetchDebt();
  }, [customerId]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 bg-black/50 transition-opacity ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`fixed inset-x-0 bottom-0 z-50 h-[90vh] rounded-t-lg bg-white shadow-lg transform transition-transform duration-300
                   ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="flex justify-between items-center px-6 py-4 border-b sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="text-xl font-bold">Financial One-Pager</h2>
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 hover:bg-blue-50 hover:text-blue-700 transition-colors duration-200"
                >
                  <Download className="h-4 w-4" />
                  <span>Download</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="animate-scale-in">
              <DropdownMenuItem
  onClick={async () => {
    try {
      await generatePdf();
    } catch (error) {
      console.error("PDF generation failed", error);
    }
  }}
>
  <FileText className="h-4 w-4 mr-2" />
  <span>Download PDF</span>
</DropdownMenuItem>

                <DropdownMenuItem onClick={() => generateDocx(CustomerData, shareDebtData,selectedDate)}>
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Download DOCX</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors duration-200"
              aria-label="Close modal"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <ScrollArea className=" h-[calc(90vh-70px)] p-6">


          <div
            ref={contentRef}
            className=" pdf-container"
            style={{
              padding: '2rem', // p-8
              maxWidth: '56rem', // max-w-4xl (896px)
              marginLeft: 'auto',
              marginRight: 'auto', // mx-auto
              lineHeight: '1.6',
              color: '#333',
              backgroundColor: '#ffffff',
              boxShadow: '0 0 20px rgba(0,0,0,0.1)',
              borderRadius: '8px',
            }}
          >
            {/* Company Logo and Header - This should be on the first page, no page break before it */}
            <div className="pdf-section text-center mb-8 pb-6 border-b-2 border-[#9e7926]">
              <div className="mb-6">

                <div >
                  <img src={Colour_Astrafin} alt="Left Image" className="w-45 h-auto rounded-full mb-[5px] -mt-[35px]" />
                </div>

                {/* <div className="w-20 h-20 bg-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                
                  <Building2 className="h-10 w-10 text-white" />
                </div> */}
                <h1 className="text-2xl font-bold text-[#9e7926] mb-2">{CustomerData?.company_details?.legal_name}</h1>
                <div className="w-24 h-1 bg-[#9e7926] mx-auto mb-3"></div>
              </div>
              <div
                style={{
                  display: 'inline-block',
                  backgroundColor: '#f9fafb',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                }}
              >
                <p
                  style={{
                    fontSize: '0.875rem', // equivalent to text-sm
                    color: 'gray',     // equivalent to text-gray-800
                  }}
                >
                  <strong style={{ fontWeight: 600 }}>Report Date:</strong>{' '}
                  {new Date().toLocaleDateString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </p>
              </div>
            </div>

            {/* Content Sections with Page Break Classes */}
            <div className="space-y-8">
              {/* Company Profile Section - Force new page, avoid breaking inside */}
              {/* 9e7926 */}
              {/* d9cba6 */}
              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <CompanyProfileSection company_details={CustomerData?.company_details} />
              </div>

              {/* Business Model Section - Force new page, avoid breaking inside */}
              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <BusinessModelSection businessSession={CustomerData?.businessSession} />
              </div>

              {/* Open Charges Section - Force new page, avoid breaking inside */}
              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <OpenChargesSection charges={CustomerData?.openChargesSession} />
              </div>

              {/* Director Details Section (which appears as Key Management People in your PDF) - Force new page, avoid breaking inside */}
              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <DirectorDetailsSection directors={CustomerData?.directorSession} />
              </div>

              {/* Key Ratios Section - Force new page, avoid breaking inside */}
              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <KeyRatiosSection financials={CustomerData?.financialSession} />
              </div>

              {/* Gst Details Section - Force new page, avoid breaking inside */}
              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <GstDetailsSection gstDetails={CustomerData?.gstSession} />
              </div>

              {/* Shareholding Section or LLP Contribution Section - Force new page, avoid breaking inside */}
              {CustomerData?.shareHoldingMoreThenFivePercentSession !== undefined ? (
                <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                  style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                  <ShareholdingSection shareholders={CustomerData?.shareHoldingMoreThenFivePercentSession} last_updated_date={CustomerData?.company_details?.last_updated_date} />
                </div>
              ) : (CustomerData?.contribution_details !== undefined) ? (
                <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                  style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                  <LLPContributionDetailsSection contributionDetails={CustomerData?.contribution_details} />
                </div>
              ) : null}

              {/* Debt Profile Section - Force new page, avoid breaking inside */}
              {shareDebtData.length > 0 && (
                <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                  style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                  <DebtProfileSection shareDebtData={shareDebtData} selectedDate={selectedDate}/>
                </div>
              )}


              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
                <SecuritiesAllotmentSection securities={CustomerData?.securities_allotment} last_updated_date={CustomerData?.company_details?.last_updated_date} />
              </div>

              <div className="pdf-section bg-[#f2efe4] p-6 rounded-lg border-l-4 border-[#9e7926]"
                style={{ breakInside: 'avoid', breakBefore: 'page' }}>
               
                <RelatedPartySection
                      holdingEntities={CustomerData?.relatedPartySection.holding_entities || null}
                      subsidiaryEntities={CustomerData?.relatedPartySection.subsidiary_entities || null}
                      associateEntities={CustomerData?.relatedPartySection.associate_entities || null}
                      jointVentures={CustomerData?.relatedPartySection.joint_ventures || null}
                      relatedPartyTransactions={CustomerData?.relatedPartySection.related_party_transactions || null}
                    />
              </div>
            </div>

            {/* Professional Footer - Force new page if previous content fills the current one, avoid breaking inside */}
            <div className=" pdf-section mt-12 pt-6 border-t-2 border-gray-200"
              style={{ breakInside: 'avoid', breakBefore: 'page' }}>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="text-center">
                  <h3 className="font-bold text-gray-800 mb-3">Disclaimer</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    This report is auto-generated from available data. Please verify all information independently before making financial decisions.
                  </p>
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <p className="text-xs text-gray-500">
                      © 2024 Astrafin Neosolutions Private Limited.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default OnePagerModal;